import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const PRIORITY_COLOR = { high: "border-rose-500/40 text-rose-300 bg-rose-500/10", normal: "border-white/10" };

export default function ApprovalQueuePage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/approvals?property_id=${propertyId}`);
    setItems(data.items);
  };
  useEffect(() => { if (propertyId) load(); }, [propertyId]);

  const decide = async (id, decision) => {
    setPending(id);
    await api.patch(`/approvals/${id}/decide`, { decision });
    toast.success(`${decision === "approved" ? "Approved" : "Denied"}`);
    setPending(null);
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Admin Tools" title="Approval Queue" subtitle="Everything waiting on your call — pets, IDs, ARC, high-value TX." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <Card key={a.id} className="border-white/10 bg-card p-5" data-testid={`approval-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="border-white/10 capitalize">{a.type.replace("_"," ")}</Badge>
                  <Badge variant="outline" className={PRIORITY_COLOR[a.priority]}>{a.priority}</Badge>
                </div>
                <div className="font-medium">{a.subject}</div>
                <div className="text-xs text-zinc-500 mt-1">Submitted {new Date(a.submitted_at).toLocaleString()}</div>
              </div>
              <Badge variant="outline" className="border-white/10 capitalize">{a.status}</Badge>
            </div>
            {a.status === "pending" && (
              <div className="mt-4 flex gap-2">
                <Button size="sm" data-testid={`approve-${a.id}`} disabled={pending === a.id} onClick={() => decide(a.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700">
                  {pending === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve"}
                </Button>
                <Button size="sm" variant="outline" data-testid={`deny-${a.id}`} onClick={() => decide(a.id, "denied")} className="border-rose-500/40 text-rose-300">Deny</Button>
              </div>
            )}
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">Queue is empty.</div>}
      </div>
    </div>
  );
}
